import { useEffect, useState } from 'react'
import { Loader2, AlertTriangle, CheckCircle2, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import {
  useGenerateFamilySummary,
  usePatientNotificationContacts,
} from '../hooks/use-family-notifications'

// ============================================================
// Props
// ============================================================

interface FamilySummaryReviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  recordId: string
  patientId: string
}

// ============================================================
// Component
// ============================================================

export function FamilySummaryReviewModal({
  open,
  onOpenChange,
  recordId,
  patientId,
}: FamilySummaryReviewModalProps) {
  const { toast } = useToast()
  const [summaryText, setSummaryText] = useState('')
  const [sent, setSent] = useState(false)
  const whatsappSendingPaused = true

  const generateMutation = useGenerateFamilySummary()
  const { data: contacts, isLoading: contactsLoading } =
    usePatientNotificationContacts(patientId)

  const isGenerating = generateMutation.isPending
  const hasNoContacts = !contactsLoading && (!contacts || contacts.length === 0)

  // Generate summary on open
  useEffect(() => {
    if (open && recordId) {
      setSent(false)
      setSummaryText('')
      generateMutation.mutate(recordId, {
        onSuccess: (data) => {
          setSummaryText(data.summary_text)
        },
        onError: (error) => {
          toast({
            title: 'Erro ao gerar resumo',
            description:
              error instanceof Error
                ? error.message
                : 'Nao foi possivel gerar o resumo. Tente novamente.',
            variant: 'destructive',
          })
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recordId])

  const handleSend = () => {
    toast({
      title: 'Envio por WhatsApp pausado',
      description:
        'Os disparos para familiares ficam bloqueados até os templates da Meta serem aprovados e liberados.',
      variant: 'destructive',
    })
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Resumo para Familia</DialogTitle>
          <DialogDescription>
            A IA gerou um resumo acessivel da nota clinica. Revise e edite sem
            disparar WhatsApp neste momento.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* Loading state */}
          {isGenerating && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
              <p className="text-sm text-gray-500">Gerando resumo...</p>
            </div>
          )}

          {/* Summary editor */}
          {!isGenerating && summaryText && !sent && (
            <>
              <Textarea
                value={summaryText}
                onChange={(e) => setSummaryText(e.target.value)}
                rows={10}
                className="resize-y"
                placeholder="Resumo para familia..."
              />

              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  Envio por WhatsApp pausado. Use este resumo apenas para
                  revisão interna enquanto os templates da Meta aguardam
                  aprovação.
                </p>
              </div>

              {/* Recipients section */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Users className="h-4 w-4" />
                  Contatos cadastrados
                </h4>

                {contactsLoading && (
                  <p className="text-sm text-gray-500">
                    Carregando contatos...
                  </p>
                )}

                {hasNoContacts && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-700">
                      Nenhum familiar com notificacoes ativas. Configure os
                      contatos do paciente.
                    </p>
                  </div>
                )}

                {contacts && contacts.length > 0 && (
                  <div className="space-y-1.5">
                    {contacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-sm"
                      >
                        <div className="w-7 h-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-xs font-medium flex-shrink-0">
                          {contact.recipient_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {contact.recipient_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {contact.whatsapp_number}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Success state */}
          {sent && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
              <p className="text-sm font-medium text-gray-700">
                Resumo enviado com sucesso!
              </p>
              <p className="text-xs text-gray-500">
                O registro foi marcado como compartilhado.
              </p>
            </div>
          )}

          {/* Generation error state */}
          {!isGenerating && !summaryText && generateMutation.isError && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <AlertTriangle className="h-8 w-8 text-red-500" />
              <p className="text-sm text-red-600">
                Nao foi possivel gerar o resumo. Tente novamente.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => generateMutation.mutate(recordId)}
              >
                Tentar novamente
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          {sent ? (
            <Button onClick={handleClose}>Fechar</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleSend}
                disabled={
                  whatsappSendingPaused ||
                  isGenerating ||
                  !summaryText.trim() ||
                  hasNoContacts
                }
                className="bg-gray-400 hover:bg-gray-400"
              >
                <AlertTriangle className="mr-2 h-4 w-4" />
                Envio pausado
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
